import { api } from "../api/Middleware";
import { LEAD_REACTIVATION_CONFIG_URL } from "../api/Urls";

/**
 * Service para gerenciamento de configurações de Follow-up de Leads
 *
 * O sistema de follow-up envia mensagens automáticas para leads
 * que entraram em contato mas não responderam mais.
 */
const LeadFollowupService = {
  /**
   * Obtém a configuração atual de follow-up do agente
   * Se não existir, o backend cria automaticamente com valores padrão
   *
   * @param {number|string} agentId - ID do agente
   * @returns {Promise<Object>} Configuração do follow-up
   */
  getConfig: async (agentId) => {
    try {
      const response = await api.get(LEAD_REACTIVATION_CONFIG_URL(agentId));
      return response.data;
    } catch (error) {
      console.error("Error fetching lead followup config:", error);
      throw error;
    }
  },

  /**
   * Atualiza a configuração de follow-up do agente
   *
   * @param {number|string} agentId - ID do agente
   * @param {Object} data - Dados da configuração
   * @param {boolean} data.enabled - Se o follow-up está ativado
   * @param {number[]} data.intervals - Array de intervalos em minutos (máx 10 itens)
   * @param {number} data.max_messages - Número máximo de mensagens (1-10)
   * @param {string} data.business_context - Contexto do negócio para personalização
   * @returns {Promise<Object>} Configuração atualizada
   */
  updateConfig: async (agentId, data) => {
    try {
      const response = await api.put(LEAD_REACTIVATION_CONFIG_URL(agentId), data);
      return response.data;
    } catch (error) {
      console.error("Error updating lead followup config:", error);
      throw error;
    }
  },
};

// Helpers para conversão de intervalos
export const IntervalHelpers = {
  /**
   * Converte minutos para formato legível
   * @param {number} minutes - Minutos
   * @returns {string} Formato legível (ex: "2 horas", "1 dia")
   */
  minutesToDisplay: (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    } else if (minutes < 1440) {
      const hours = minutes / 60;
      return hours === 1 ? "1 hora" : `${hours} horas`;
    } else {
      const days = minutes / 1440;
      return days === 1 ? "1 dia" : `${days} dias`;
    }
  },

  /**
   * Converte horas para minutos
   * @param {number} hours - Horas
   * @returns {number} Minutos
   */
  hoursToMinutes: (hours) => hours * 60,

  /**
   * Converte dias para minutos
   * @param {number} days - Dias
   * @returns {number} Minutos
   */
  daysToMinutes: (days) => days * 1440,

  /**
   * Converte minutos para a melhor unidade (min, horas ou dias)
   * @param {number} minutes - Minutos
   * @returns {{ value: number, unit: string }} Valor e unidade
   */
  minutesToBestUnit: (minutes) => {
    if (minutes >= 1440 && minutes % 1440 === 0) {
      return { value: minutes / 1440, unit: 'days' };
    } else if (minutes >= 60 && minutes % 60 === 0) {
      return { value: minutes / 60, unit: 'hours' };
    }
    return { value: minutes, unit: 'minutes' };
  },

  /**
   * Converte valor e unidade para minutos
   * @param {number} value - Valor numérico
   * @param {string} unit - Unidade (minutes, hours, days)
   * @returns {number} Minutos
   */
  toMinutes: (value, unit) => {
    switch (unit) {
      case 'hours':
        return value * 60;
      case 'days':
        return value * 1440;
      default:
        return value;
    }
  },

  /**
   * Presets de intervalos disponíveis
   */
  presets: {
    fast: {
      name: 'Rápido',
      description: '30min, 1h, 2h, 4h',
      intervals: [30, 60, 120, 240],
    },
    moderate: {
      name: 'Moderado',
      description: '2h, 4h, 8h, 24h',
      intervals: [120, 240, 480, 1440],
    },
    conservative: {
      name: 'Conservador',
      description: '4h, 24h, 48h, 72h',
      intervals: [240, 1440, 2880, 4320],
    },
    weekly: {
      name: 'Semanal',
      description: '24h, 48h, 72h, 96h, 168h',
      intervals: [1440, 2880, 4320, 5760, 10080],
    },
  },

  /**
   * Valida um intervalo
   * @param {number} minutes - Intervalo em minutos
   * @returns {{ valid: boolean, error?: string }}
   */
  validateInterval: (minutes) => {
    if (minutes < 5) {
      return { valid: false, error: 'Intervalo mínimo é 5 minutos' };
    }
    if (minutes > 10080) {
      return { valid: false, error: 'Intervalo máximo é 7 dias' };
    }
    return { valid: true };
  },
};

export default LeadFollowupService;
